// The catalog's reading order. The backend returns dashboards `ORDER BY title`
// (`service/bi/repo/bi_registry.py`), which says nothing about which matter.
//
// The order lives here because the registry IR has no ordering field. Adding
// one is a wire-contract change plus a sync. If the IR gains one, this module
// is what it replaces.
//
// MATCHED BY SLUG, never by title: the slug is the routing identity (`/d/$slug`)
// and survives a rename. A title match would silently drop a renamed dashboard
// to the bottom.

/** The dashboards that lead the catalog, in the order they should read.
 *
 *  1. `mira-product-measurement` — the one the team opens by default.
 *  2. The four built from the Analytics Events Specification (product's
 *     2026-07-20 requirements doc), in the order that document's own sections
 *     introduce them: Conversation, User Behavior, Flow, Seller Results.
 *     "Quality" is not a fifth dashboard — it shares one with Conversation
 *     (`conversations-quality`) — and "Core Events" is the doc's reference
 *     list of event names, not a dashboard at all.
 *
 *  Everything else follows, in the backend's own alphabetical order. A slug
 *  listed here that no longer exists is simply skipped, so pruning a
 *  dashboard upstream cannot leave a hole or an error. */
export const FEATURED_DASHBOARD_SLUGS: readonly string[] = [
  "mira-product-measurement",
  "conversations-quality",
  "user-behavior",
  "assistant-flow-funnel",
  "seller-results",
];

/** `rows` reordered: the featured slugs first, in `FEATURED_DASHBOARD_SLUGS`
 *  order, then everything else in the order it arrived (the backend's
 *  alphabetical-by-title). Never drops, never duplicates — the result is
 *  always a permutation of the input, which is what keeps a dashboard from
 *  vanishing off the catalog because someone edited this list. */
export function orderDashboards<T extends { slug: string }>(rows: readonly T[]): T[] {
  const rank = new Map(FEATURED_DASHBOARD_SLUGS.map((slug, index) => [slug, index]));
  const featured: T[] = [];
  const rest: T[] = [];
  for (const row of rows) {
    if (rank.has(row.slug)) featured.push(row);
    else rest.push(row);
  }
  featured.sort((a, b) => (rank.get(a.slug) as number) - (rank.get(b.slug) as number));
  return [...featured, ...rest];
}
